import { Controller } from "@hotwired/stimulus"

/**
 * Polls a JSON endpoint while the AI generates project title/description.
 * When the endpoint reports generation is complete, reloads the page via
 * Turbo so the form shows the pre-filled fields.
 *
 * Usage:
 *   <div data-controller="description-loader"
 *        data-description-loader-url-value="/app/profile/project_description_status">
 *     <!-- shimmer skeleton -->
 *   </div>
 */
export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 2000 },
    maxAttempts: { type: Number, default: 15 },
  }

  declare urlValue: string
  declare intervalValue: number
  declare maxAttemptsValue: number

  private timer: ReturnType<typeof setTimeout> | null = null
  private attempts = 0

  connect() {
    this.poll()
  }

  disconnect() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private async poll() {
    this.attempts++

    if (this.attempts > this.maxAttemptsValue) {
      // Give up — reload page once so user sees normal empty fields
      window.location.reload()
      return
    }

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
      })
      const data = await response.json()

      if (!data.generating) {
        // Fields are populated — reload page to show them in the form
        window.location.reload()
        return
      }
    } catch {
      // Ignore transient fetch errors, retry on next tick
    }

    this.timer = setTimeout(() => this.poll(), this.intervalValue)
  }
}
